import { Currency, DeliveryStatus, Locale, StockStatus } from '@synergy/models';
import { getLocalizedString } from '@synergy/utils';

import { ArticleDetail } from '../article-detail';
import { ArticleDto } from '../dto/article-dto';
import { mapArticleVariant } from './article-variant-mapper';
import { mapBonusCondition } from './bonus-condition-mapper';
import { mapPackingUnit } from './packing-unit-mapper';
import { mapSupplier } from './supplier-mapper';

export const mapArticleDetail = (locales: Locale[], currency: Currency, article: ArticleDto): ArticleDetail | null => {
    const currencyPrice = article?.prices.find(price => price.currency === currency);

    // HINT: should not happen, because mainPrice is required
    if (!currencyPrice) {
        return null;
    }

    const imageFormat = '620x620';

    const orderUnit = getLocalizedString(locales, article.orderUnit);
    const localizedContentUnit = getLocalizedString(locales, article.contentUnit);
    const isContentUnitSameAsOrderUnit = localizedContentUnit === orderUnit;
    const contentUnit = isContentUnitSameAsOrderUnit ? undefined : localizedContentUnit;
    const contentQuantity = isContentUnitSameAsOrderUnit ? undefined : article.contentQuantity;

    return {
        id: article.id,
        name: getLocalizedString(locales, article.name),
        number: article.number,
        deliveryStatus: article.deliveryStatus,
        mainPrice: currencyPrice.mainPrice,
        scalePrices: currencyPrice.scalePrices ?? [],
        specialPrice: currencyPrice.specialPrices?.[0],
        orderUnit,
        contentUnit,
        variants: article.variants?.map(variant => mapArticleVariant(locales, currency, variant)) ?? [],
        documents: article.documents,
        images: article.images.map(image => image[imageFormat] ?? '').filter(image => !!image),
        attributes: article.attributes ?? [],
        supplier: mapSupplier(locales, article.supplier),
        stockStatus: article.stockStatus,
        globalTradeItemNumber: article.globalTradeItemNumber ?? '',
        manufacturer: article.manufacturer ?? '',
        manufacturerNumber: article.manufacturerNumber ?? '',
        brand: article.brand ?? '',
        contentQuantity,
        priceQuantity: article.priceQuantity,
        priceReferenceQuantity: article.priceReferenceQuantity,
        minimumOrderQuantity: article.minimumOrderQuantity,
        maximumOrderQuantity: article.maximumOrderQuantity,
        recommendedOrderQuantity: article.recommendedOrderQuantity,
        quantityInterval: article.quantityInterval ?? 1,
        deliveryTimeInDays: article.deliveryTimeInDays,
        supplierConditions: article.supplier.conditions?.map(condition => mapBonusCondition(condition)) ?? [],
        productDescription:
            getLocalizedString(locales, article?.longDescription) ||
            getLocalizedString(locales, article?.shortDescription) ||
            '',
        isBestPrice: article.isBestPrice ?? false,
        isEcoProduct: article.isEcoProduct ?? false,
        inventory: article.inventory,
        currency,
        isAvailable:
            article.deliveryStatus !== DeliveryStatus.Unavailable &&
            article.deliveryStatus !== DeliveryStatus.OnRequest &&
            article.stockStatus !== StockStatus.SoldOut,
        isFavorite: article.isFavorite ?? false, // TODO: remove optionality after backend implementation
        packingUnits: article.packingUnits.map(packingUnit => mapPackingUnit(locales, packingUnit)),
    };
};
